import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { Calculator, CheckCircle2, Play, RefreshCcw, XCircle, AlertTriangle } from "lucide-react";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import { postRequest, API_BASE_URL, extractErrorMessage } from "../../../../api";
import "./PaieCyclePages.css";

const userNameCalc = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const CalculPaiePage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [form, setForm] = useState({ periodeId: "", userId: "", nombreJoursTravailles: 26, modePaiement: "VIREMENT" });
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState([]);
    const [calculating, setCalculating] = useState(false);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("periodes"));
    }, [dispatch]);

    const activeUsers = useMemo(() => {
        return users.filter(u => u.isActive !== false);
    }, [users]);

    const calculate = async () => {
        if (!form.periodeId) {
            Swal.fire({ icon: "warning", text: "Sélectionnez une période." });
            return;
        }

        setProgress(0);
        setLogs([]);
        setCalculating(true);

        const token = sessionStorage.getItem("token") || localStorage.getItem("token");

        // 1. Single Collaborator Recalculation
        if (form.userId) {
            const targetUser = users.find(u => String(u.id) === String(form.userId));
            const name = targetUser ? userNameCalc(targetUser) : `#${form.userId}`;

            setLogs([{ type: "info", text: `Début du calcul pour ${name}...` }]);

            try {
                const response = await postRequest({
                    url: `${API_BASE_URL}/api/bulletins-paie/calculate`,
                    data: {
                        userId: Number(form.userId),
                        periodeId: Number(form.periodeId),
                        nombreJoursTravailles: Number(form.nombreJoursTravailles),
                        modePaiement: form.modePaiement,
                    },
                    token
                });
                
                setProgress(100);
                setLogs([
                    { type: "success", text: `Calcul réussi pour ${name}.` },
                    { type: "success", text: `Net à payer : ${Number(response.data?.netAPayer || 0).toLocaleString("fr-FR")} MAD (Brut : ${Number(response.data?.salaireBrutImposable || 0).toLocaleString("fr-FR")} MAD).` }
                ]);
                
                Swal.fire({
                    icon: "success",
                    title: "Calcul terminé",
                    text: `Le bulletin de ${name} a été calculé avec succès.`,
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (error) {
                const msg = extractErrorMessage(error, "Erreur inconnue");
                setProgress(100);
                setLogs([
                    { type: "error", text: `Échec du calcul pour ${name}.` },
                    { type: "error", text: `Détail : ${msg}` }
                ]);
                Swal.fire({
                    icon: "error",
                    title: "Calcul impossible",
                    text: msg
                });
            }
            setCalculating(false);
            return;
        }

        // 2. Bulk/Global Calculation
        const targetUsersList = activeUsers;
        if (targetUsersList.length === 0) {
            setLogs([{ type: "error", text: "Aucun collaborateur actif trouvé dans l'entreprise." }]);
            setCalculating(false);
            return;
        }

        setLogs([{ type: "info", text: `Initialisation du calcul pour ${targetUsersList.length} collaborateurs...` }]);

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < targetUsersList.length; i++) {
            const user = targetUsersList[i];
            const name = userNameCalc(user);

            try {
                const response = await postRequest({
                    url: `${API_BASE_URL}/api/bulletins-paie/calculate`,
                    data: {
                        userId: user.id,
                        periodeId: Number(form.periodeId),
                        nombreJoursTravailles: Number(form.nombreJoursTravailles),
                        modePaiement: form.modePaiement,
                    },
                    token
                });
                successCount++;
                setLogs(prev => [
                    ...prev,
                    { type: "success", text: `${name} : Bulletin calculé avec succès. Net : ${Number(response.data?.netAPayer || 0).toLocaleString("fr-FR")} MAD.` }
                ]);
            } catch (error) {
                failCount++;
                const msg = extractErrorMessage(error, "Erreur");
                setLogs(prev => [
                    ...prev,
                    { type: "error", text: `${name} : Échec du calcul - ${msg}.` }
                ]);
            }

            setProgress(Math.round(((i + 1) / targetUsersList.length) * 100));
        }

        setLogs(prev => [
            ...prev,
            { type: "info", text: `Traitement terminé. Succès: ${successCount}, Échecs: ${failCount}.` }
        ]);

        Swal.fire({
            icon: failCount > 0 ? "warning" : "success",
            title: "Calcul global terminé",
            text: `${successCount} bulletin(s) calculé(s) avec succès. ${failCount} échec(s).`
        });

        setCalculating(false);
    };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><Calculator size={18} />Calcul de la paie</h2>
                    <p>Lancement du calcul par période, pour tous les collaborateurs ou un collaborateur ciblé.</p>
                </div>
                <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => dispatch(payrollFetchResourceRequest("periodes"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>
            
            <div className="pcyc-grid pcyc-grid-form">
                <section className="pcyc-card">
                    <div className="pcyc-card-title"><Play size={16} />Paramètres de calcul</div>
                    <div className="pcyc-form-grid">
                        <label>
                            <span>Période</span>
                            <select value={form.periodeId} onChange={(e) => setForm({ ...form, periodeId: e.target.value })} disabled={calculating}>
                                <option value="">Sélectionner</option>
                                {(payroll.periodes || []).map((p) => (
                                    <option key={p.id} value={p.id} disabled={p.statut === "CLOTUREE"}>
                                        {String(p.mois).padStart(2, "0")}/{p.annee} - {p.statut} {p.statut === "CLOTUREE" ? "(Déjà clôturée)" : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Collaborateur cible</span>
                            <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} disabled={calculating}>
                                <option value="">Tous les collaborateurs ({activeUsers.length})</option>
                                {activeUsers.map((u) => (
                                    <option key={u.id} value={u.id}>{userNameCalc(u)}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Jours travaillés (Défaut)</span>
                            <input type="number" step="0.01" value={form.nombreJoursTravailles} onChange={(e) => setForm({ ...form, nombreJoursTravailles: e.target.value })} disabled={calculating} />
                        </label>
                        <label>
                            <span>Mode paiement (Défaut)</span>
                            <select value={form.modePaiement} onChange={(e) => setForm({ ...form, modePaiement: e.target.value })} disabled={calculating}>
                                <option value="VIREMENT">VIREMENT</option>
                                <option value="CHEQUE">CHEQUE</option>
                                <option value="ESPECES">ESPECES</option>
                            </select>
                        </label>
                    </div>
                    <div className="pcyc-actions">
                        <button className="pcyc-btn pcyc-btn-primary" type="button" onClick={calculate} disabled={calculating}>
                            <Play size={15} /> Lancer le calcul
                        </button>
                    </div>
                </section>
                
                <section className="pcyc-card">
                    <div className="pcyc-card-title">Progression de l'exécution</div>
                    <div className="pcyc-progress">
                        <span style={{ width: `${progress}%` }} />
                    </div>
                    <strong className="pcyc-progress-value">{progress}%</strong>
                    <div className="pcyc-logs" style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #e5e7eb", padding: "10px", borderRadius: "8px" }}>
                        {logs.length === 0 ? (
                            <p className="pcyc-help">Aucun calcul en cours. Veuillez lancer une exécution.</p>
                        ) : (
                            logs.map((log, index) => (
                                <div key={index} className={`pcyc-log ${log.type}`}>
                                    {log.type === "success" && <CheckCircle2 size={14} />}
                                    {log.type === "error" && <XCircle size={14} />}
                                    {log.type === "info" && <RefreshCcw size={14} className="kms-spin" />}
                                    <span>{log.text}</span>
                                </div>
                            ))
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default CalculPaiePage;
